/**
 * 権限付与画面 (権限管理 SBI-8)。
 *
 * ユーザーごとに所属する権限グループを複数選択して保存する。ユーザー一覧 (`GET /users`)、グループ一覧
 * (`GET /permission-groups`)、機能カタログ (`GET /features`) を読み込み、行の「権限を編集」で
 * モーダル ({@link PermissionAssignModal}) を開く。ユーザー自体の追加 / 削除は「ユーザー管理」画面で行う。
 *
 * ⚠️ この画面の表示制御 (`permission_assign`) は利便性のため。最終的な拒否は常にバックエンド (403)。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { PermissionGroupsClient } from '../api/permissionGroups';
import type { AdminUser, PermissionFeature, PermissionGroup } from '../api/types';
import { UsersClient } from '../api/users';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { groupNamesLabel, userDisplayName } from '../permissions/adminLabels';

import { NoticeDialog } from './NoticeDialog';
import { PermissionAssignModal } from './PermissionAssignModal';
import { describeAdminError } from './permissionAdminMessages';
import { StatusBanner } from './StatusBanner';

export interface PermissionAssignScreenProps {
  config: AppConfig;
}

export function PermissionAssignScreen({ config }: PermissionAssignScreenProps): React.JSX.Element {
  const { getIdToken } = useAuth();
  const usersClient = useMemo(() => new UsersClient({ getIdToken, config }), [getIdToken, config]);
  const groupsClient = useMemo(
    () => new PermissionGroupsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [features, setFeatures] = useState<PermissionFeature[]>([]);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  /** ユーザー / グループ / 機能カタログをまとめて取得する (どれかが失敗したらエラーを出し、取れた分だけ反映する)。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    const results = await Promise.allSettled([
      usersClient.list(),
      groupsClient.list(),
      groupsClient.listFeatures(),
    ]);
    if (currentRequestId !== requestIdRef.current) return;
    const [usersResult, groupsResult, featuresResult] = results;
    if (usersResult.status === 'fulfilled') setUsers(usersResult.value.items);
    if (groupsResult.status === 'fulfilled') setGroups(groupsResult.value.items);
    if (featuresResult.status === 'fulfilled') setFeatures(featuresResult.value.items);
    const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failed) setErrorMessage(describeAdminError(failed.reason).message);
    setLoading(false);
  }, [groupsClient, usersClient]);

  useEffect(() => {
    void reload();
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  const handleSaved = useCallback(
    (user: AdminUser, groupCount: number): void => {
      setEditing(null);
      setNoticeMessage(
        groupCount === 0
          ? `${userDisplayName(user)} の所属をすべて外しました (権限なし)`
          : `${userDisplayName(user)} の権限を保存しました (${groupCount} グループ)`,
      );
      void reload();
    },
    [reload],
  );

  /** 編集中に他者がユーザーを削除していた: モーダルを閉じて一覧を取り直す。 */
  const handleDeletedByOthers = useCallback((): void => {
    setEditing(null);
    void reload();
  }, [reload]);

  return (
    <div className="permission-admin-page">
      <section className="section" aria-label="権限付与" data-testid="permission-assign-screen">
        <div className="route-selector" data-testid="permission-assign-toolbar">
          <h2 className="permission-admin-title">権限付与</h2>
          <div className="actions">
            <button
              type="button"
              data-testid="permission-assign-reload"
              onClick={() => void reload()}
              disabled={loading}
            >
              再読み込み
            </button>
          </div>
        </div>
      </section>

      <section className="section permission-admin-list-section">
        <div className="permission-admin-list-body" data-testid="permission-assign-list-body">
          {errorMessage ? (
            <StatusBanner
              variant="error"
              onDismiss={() => setErrorMessage(null)}
              data-testid="permission-assign-error"
            >
              {errorMessage}
            </StatusBanner>
          ) : null}

          <table
            className="points-table permission-admin-table"
            data-testid="permission-assign-table"
          >
            <colgroup>
              <col className="permission-admin-col-email" />
              <col />
              <col className="permission-admin-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>メールアドレス</th>
                <th>所属グループ</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !loading ? (
                <tr>
                  <td colSpan={3} className="empty-state" data-testid="permission-assign-empty">
                    登録されたユーザーはありません
                  </td>
                </tr>
              ) : null}
              {users.map((user) => (
                <tr key={user.user_sub} data-testid={`permission-assign-row-${user.user_sub}`}>
                  <td>{userDisplayName(user)}</td>
                  <td data-testid={`permission-assign-groups-${user.user_sub}`}>
                    {groupNamesLabel(user.permission_groups)}
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      onClick={() => setEditing(user)}
                      disabled={loading}
                      data-testid={`permission-assign-edit-${user.user_sub}`}
                    >
                      権限を編集
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editing ? (
        <PermissionAssignModal
          key={editing.user_sub}
          user={editing}
          groups={groups}
          features={features}
          apiClient={usersClient}
          onSaved={handleSaved}
          onDeletedByOthers={handleDeletedByOthers}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <NoticeDialog
        open={noticeMessage !== null}
        message={noticeMessage ?? ''}
        onClose={() => setNoticeMessage(null)}
      />
    </div>
  );
}
